import {useState, useEffect, useRef} from "react";
import {Link, useLocation} from "react-router-dom";
import {success, error} from "../Global/toast";
import clsx from "clsx";
import APICallService from "../api/apiCallService";
import {CONTACTUS} from "../api/apiEndPoints";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import ChatIcon from "../assets/media/chat-icon.svg";
import Mail from "../assets/media/mail.svg";

interface FormData {
    name: string;
    email: string;
    message: string;
}
interface validationErrors {
    name: boolean;
    email: boolean;
    message: boolean;
}

const ContactUs = () => {
    const location = useLocation();

    const initialState = {
        name: "",
        email: "",
        message: "",
    };

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<FormData | any>(initialState);
    const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
    const recaptchaRef = useRef<HTMLDivElement>(null);
    const recaptchaWidgetId = useRef<number | null>(null);
    const recaptchaRenderedRef = useRef<boolean>(false);

    const [validationErrors, setValidationErrors] = useState<validationErrors>({
        name: false,
        email: false,
        message: false,
    });

    useEffect(() => {
        setValidationErrors({
            name: false,
            email: false,
            message: false,
        });
    }, [location.pathname]);

    useEffect(() => {
        (window as any).onRecaptchaSuccessContact = (token: string) => {
            setRecaptchaToken(token);
        };

        (window as any).onRecaptchaErrorContact = () => {
            setRecaptchaToken(null);
        };

        (window as any).onRecaptchaExpiredContact = () => {
            setRecaptchaToken(null);
        };

        const renderRecaptcha = () => {
            if (recaptchaRenderedRef.current) {
                return;
            }

            if (recaptchaRef.current && (window as any).grecaptcha && (window as any).grecaptcha.render) {
                if (recaptchaWidgetId.current !== null) {
                    return;
                }

                if (recaptchaRef.current.children.length > 0) {
                    recaptchaRenderedRef.current = true;
                    return;
                }

                try {
                    recaptchaWidgetId.current = (window as any).grecaptcha.render(recaptchaRef.current, {
                        sitekey: "6Lemrh8sAAAAALg4miyU4pczrrhZEKpxQnfYeOr-",
                        callback: (token: string) => {
                            setRecaptchaToken(token);
                        },
                        "expired-callback": () => {
                            setRecaptchaToken(null);
                        },
                        "error-callback": () => {
                            setRecaptchaToken(null);
                        },
                    });
                    recaptchaRenderedRef.current = true;
                } catch (error) {
                    console.error("Error rendering reCAPTCHA:", error);
                }
            }
        };

        if ((window as any).grecaptcha && (window as any).grecaptcha.render) {
            renderRecaptcha();
        } else {
            const checkRecaptcha = setInterval(() => {
                if ((window as any).grecaptcha && (window as any).grecaptcha.render) {
                    clearInterval(checkRecaptcha);
                    renderRecaptcha();
                }
            }, 100);

            setTimeout(() => {
                clearInterval(checkRecaptcha);
            }, 10000);
        }

        return () => {
            // Cleanup
            if (recaptchaWidgetId.current !== null && (window as any).grecaptcha) {
                try {
                    (window as any).grecaptcha.reset(recaptchaWidgetId.current);
                } catch (e) {}
            }
            delete (window as any).onRecaptchaSuccessContact;
            delete (window as any).onRecaptchaErrorContact;
            delete (window as any).onRecaptchaExpiredContact;
        };
    }, []);

    const handleInputChange = (value: string, field: keyof FormData) => {
        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
        const nameRegex = /^[a-zA-Z\s]+$/;

        let isInvalid = false;
        if (field === "name") {
            isInvalid = value.trim().length === 0 || !nameRegex.test(value.trim());
        } else if (field === "email") {
            isInvalid = value.trim().length === 0 || !emailRegex.test(value.trim());
        } else {
            isInvalid = value.trim().length === 0;
        }

        setValidationErrors((prev) => ({
            ...prev,
            [field]: isInvalid,
        }));

        setFormData((prev: FormData) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            const { name, email, message } = formData;

            const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
            const nameRegex = /^[a-zA-Z\s]+$/;

            const errors: validationErrors = {
                name: name.trim().length === 0 || !nameRegex.test(name.trim()),
                email: !emailRegex.test(email.trim()),
                message: message.trim().length === 0,
            };

            setValidationErrors(errors);

            const isValid = Object.values(errors).every((error) => !error);

            if (!isValid) return;

            if (!recaptchaToken) {
                error("Please complete the reCAPTCHA verification.");
                return;
            }

            setLoading(true);

            const apiService = new APICallService(CONTACTUS, {
                ...formData,
                recaptchaToken: recaptchaToken
            });
            const response = await apiService.callAPI();

            if (response) {
                setFormData({
                    name: "",
                    email: "",
                    message: "",
                });
                setValidationErrors({
                    name: false,
                    email: false,
                    message: false,
                });
                setRecaptchaToken(null);
                if (recaptchaWidgetId.current !== null && (window as any).grecaptcha) {
                    try {
                        (window as any).grecaptcha.reset(recaptchaWidgetId.current);
                    } catch (e) { }
                }
                success("Contact details submitted successfully!");
            }
        } catch (err) {
            console.error("Submission error:", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div id="home" className="main-hero-sec wrapper overflow-hidden bg-cultured ">
                <div
                    id="contact-us"
                    className="wrapper section-padding position-relative bg-cultured contact-us-sec z-0"
                >
                    <div className="">
                        <div className="container">
                            <div className="row align-items-center gy-8 gx-xl-14">
                                <div className="col-lg-6">
                                    <img
                                        className="img-fluid mb-8 mb-md-9  mb-lg-11"
                                        src={ChatIcon}
                                        width={86}
                                        height={86}
                                        alt=""
                                        loading="lazy"
                                    />
                                    <h2 className="fs-50 fw-bolder text-dark-primary mb-3 main-ease-animator1">
                                        Let's <span className="underline-3 style-3 green">Connect</span>
                                    </h2>
                                    <p className="fs-22 fw-medium text-dark-primary mb-8 mb-md-10 mb-lg-12">
                                        We'd love to hear from you! If you have any questions, suggestions, or feedback,
                                        please reach out to us.
                                    </p>
                                    <div className="d-flex align-items-center gap-3">
                                        <img
                                            className="img-fluid flex-shrink-0"
                                            src={Mail}
                                            width={46}
                                            height={46}
                                            alt=""
                                            loading="lazy"
                                        />
                                        <Link
                                            to="mailto:support@feedmycycle.com"
                                            className="fs-22 fw-normal text-dark-primary"
                                        >
                                            support@feedmycycle.com
                                        </Link>
                                    </div>
                                </div>
                                <div className="col-lg-6">
                                    <div className="card bg-primary br-30">
                                        <div className="card-body p-md-7 p-lg-10">
                                            <Form onSubmit={handleSubmit} noValidate>
                                                <div className="row gy-4 gy-lg-7">
                                                    <div className="col-12">
                                                        <Form.Group controlId="footerContactName">
                                                            <Form.Label className="text-white">Name</Form.Label>
                                                            <Form.Control
                                                                type="text"
                                                                className={clsx(
                                                                    "form-control bg-white",
                                                                    validationErrors.name ? "border-danger" : ""
                                                                )}
                                                                placeholder="John Doe"
                                                                disabled={loading}
                                                                onChange={(e) =>
                                                                    handleInputChange(e.target.value, "name")
                                                                }
                                                                value={formData.name}
                                                                autoComplete="off"
                                                            />
                                                        </Form.Group>
                                                    </div>
                                                    <div className="col-12">
                                                        <Form.Group controlId="footerContactEmail">
                                                            <Form.Label className="text-white">Email</Form.Label>
                                                            <Form.Control
                                                                type="text"
                                                                className={clsx(
                                                                    "form-control bg-white",
                                                                    validationErrors.email ? "border-danger" : ""
                                                                )}
                                                                placeholder="john@mail.com"
                                                                onChange={(e) =>
                                                                    handleInputChange(e.target.value, "email")
                                                                }
                                                                disabled={loading}
                                                                value={formData.email}
                                                                autoComplete="off"
                                                            />
                                                        </Form.Group>
                                                    </div>
                                                    <div className="col-12">
                                                        <Form.Group controlId="footerContactMessage">
                                                            <Form.Label className="text-white">Message</Form.Label>
                                                            <Form.Control
                                                                as="textarea"
                                                                rows={4}
                                                                className={clsx(
                                                                    "form-control bg-white",
                                                                    validationErrors.message ? "border-danger" : ""
                                                                )}
                                                                placeholder="Hello there"
                                                                onChange={(e) =>
                                                                    handleInputChange(e.target.value, "message")
                                                                }
                                                                disabled={loading}
                                                                value={formData.message}
                                                            />
                                                        </Form.Group>
                                                    </div>
                                                    <div className="col-12">
                                                        <div ref={recaptchaRef}></div>
                                                    </div>
                                                    <div className="col-12 mt-7">
                                                        <Button
                                                            type="submit"
                                                            className="btn btn-white py-lg-7 w-100"
                                                            disabled={loading}
                                                        >
                                                            Submit
                                                        </Button>
                                                    </div>
                                                </div>
                                            </Form>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ContactUs;
